import { path } from './client'
import type { ApiClient, DownloadedFile, Query } from './client'
import type { Instant } from './types'

/**
 * Exports.
 *
 * Every call here goes through `download`, not `request`, and that is the
 * reason the module exists rather than these being three more methods
 * elsewhere: an export is bytes, and running a CSV through `JSON.parse` gives
 * `null` with no error anywhere. A report that silently downloads an empty file
 * is a report that gets sent to a donor.
 *
 * `iati` is a format rather than a report kind, because the whole point of
 * shaping indicators after the IATI `result` element was that the export is a
 * serializer over the same rows the console already shows. A separate report
 * kind would mean a second query that can disagree with the first.
 */

export type ReportKind =
  | 'indicators'
  | 'broadcasts'
  | 'cases'
  | 'tasks'
  | 'conversations'
  | 'loops'
  | 'audit'

export type ReportFormat = 'csv' | 'json' | 'iati'

export interface ReportQuery extends Query {
  projectId?: string
  from?: Instant
  to?: Instant
  format?: ReportFormat
}

export function reportsApi(http: ApiClient) {
  return {
    /**
     * Fetches an export as a file.
     *
     * The blob is returned rather than being handed to the browser here. Saving
     * it is a DOM act (an anchor, an object URL, a revoke) and it belongs in
     * the screen that has a button, not in the API layer, which is used from
     * tests that have no document.
     */
    download(kind: ReportKind, query: ReportQuery = {}): Promise<DownloadedFile> {
      return http.download(path`/v1/org/reports/${kind}`, { query })
    },
  }
}

export type ReportsApi = ReturnType<typeof reportsApi>
